// Official games and games engine, imported as text: the client runs them in an isolated worker.
// Separate subpath (@gruvetto/creations/games): the API does not need them and does not bundle them.
import type { CreationOf } from '@gruvetto/contracts';
import { GRUVETTO, type Entry } from '../catalog.js';
import dynamo from './dynamo.js?raw';
import zeppelin from './zeppelin.js?raw';
import neon from './neon.js?raw';
import cadence from './cadence.js?raw';
import horde from './horde.js?raw';
import pong from './pong.js?raw';

export { GAME_RUNTIME, GAME_CODE_OFFSET } from './runtime.js';

type GameMeta = Omit<CreationOf<'game'>, 'content'> & { content: Omit<CreationOf<'game'>['content'], 'code'> };
const official = (id: string, code: string, meta: GameMeta): Entry<'game'> => ({
  id, version: 1, source: 'builtin', author: GRUVETTO, updatedAt: 0,
  creation: { ...meta, content: { ...meta.content, code } },
});

export const OFFICIAL_GAMES: Entry<'game'>[] = [
  official('dynamo', dynamo, {
    kind: 'game', name: 'Dynamo Defense', tags: ['tower defense', 'strategy'],
    desc: 'Your watts power the grid: build towers, fend off 15 waves. The bigger your defence, the harder the trainer resists. Sprint to overload.',
    content: {
      mechanics: ['batterie', 'explosion', 'charge'], workout: true, duration: 1200,
      controls: [{ action: 'left', label: 'Previous slot' }, { action: 'right', label: 'Next slot' }, { action: 'up', label: 'Previous tower type' }, { action: 'down', label: 'Next tower type' }, { action: 'fire', label: 'Build or upgrade' }],
    },
  }),
  official('zeppelin', zeppelin, {
    kind: 'game', name: 'Zeppelin', tags: ['arcade', 'workout'],
    desc: 'Your power sets the airship’s altitude between mountains, storms and birds. In a workout, holding the target means flying in the golden corridor.',
    content: { mechanics: ['moteur', 'zone'], workout: true, duration: 900, controls: [{ action: 'fire', label: 'Drop ballast' }] },
  }),
  official('neon', neon, {
    kind: 'game', name: 'Neon Pedals', tags: ['arcade', 'duel'],
    desc: 'Light cycles against three riders: your watts set the speed, your trail boxes the others in. Jump a wall, sprint for the turbo.',
    content: {
      mechanics: ['moteur', 'explosion', 'zone'], workout: true, duration: 720,
      controls: [{ action: 'left', label: 'Turn left' }, { action: 'right', label: 'Turn right' }, { action: 'fire', label: 'Jump over a wall' }],
    },
  }),
  official('cadence', cadence, {
    kind: 'game', name: 'Cadence Hero', tags: ['rhythm', 'music'],
    desc: 'Rhythm game: hold the requested cadence, play the notes on the beat with the controllers and sprint on the lightning bolts. Pushing hard stiffens the resistance.',
    content: {
      mechanics: ['rythme', 'explosion', 'charge'], workout: true, duration: 600,
      controls: [{ action: 'left', label: 'Blue note' }, { action: 'fire', label: 'Yellow note' }, { action: 'right', label: 'Pink note' }],
    },
  }),
  official('horde', horde, {
    kind: 'game', name: 'Horde', tags: ['survival', 'chase'],
    desc: 'A zombie horde chases you all night: your watts set your speed, the hills bring it back. Dodge, throw firecrackers and hold out until dawn.',
    content: {
      mechanics: ['moteur', 'charge', 'explosion'], workout: true, duration: 900,
      controls: [{ action: 'up', label: 'Upper lane' }, { action: 'down', label: 'Lower lane' }, { action: 'fire', label: 'Throw a firecracker' }],
    },
  }),
  official('pong', pong, {
    kind: 'game', name: 'Roller Pong', tags: ['arcade', 'duel'],
    desc: 'A pong against the computer where your paddle height is your power: high balls are caught by sprinting. Fire to smash.',
    content: {
      mechanics: ['moteur', 'zone', 'explosion'], workout: true, duration: 900,
      controls: [{ action: 'up', label: 'Raise the paddle a little' }, { action: 'down', label: 'Lower the paddle a little' }, { action: 'fire', label: 'Smash' }],
    },
  }),
];
export { official as officialGame };
